/**
 * KPI Executive Brief view-model — the native counterpart of xfactor's
 * `src/lib/exec-brief-kpi/extract.core.ts`, read off the Pass-2
 * `executiveBrief` block that render_account_detail returns on
 * `briefing.overall` (xfactor's overall json_schema, see
 * backend/services/kpi_prompts/xfactor_kpi_aiconfig_v1.json).
 *
 * Same rule as the Situation Card: the computed scores on the pane's
 * `bubble` win, and the model's own echo in `situationAtAGlance.scores` is
 * only a fallback. List caps mirror the schema's maxItems.
 */

import { isDict } from "./derive";
import type {
  XFactorMove,
  XFactorPoint,
  XFactorScoreTile,
} from "../../../Types/XFactorViewsRendering";

export type KpiExecBriefCoverageRow = {
  category: string;
  status: string | null;
  note: string | null;
};

export type KpiExecBriefScorecardRow = {
  category: string;
  score: number | null;
  trend: string | null;
  weight: number | null;
  readThrough: string | null;
};

export type KpiExecBriefKeyNumber = {
  signal: string;
  reading: string;
  direction: "up" | "down" | "flat" | "unknown" | null;
  category: string | null;
};

export type KpiExecBriefModel = {
  present: boolean;
  asOf: string | null;
  headline: string | null;
  whyTheseScores: string | null;
  tiles: XFactorScoreTile[];
  landscapeText: string | null;
  coverage: KpiExecBriefCoverageRow[];
  scorecard: KpiExecBriefScorecardRow[];
  healthTrend: string | null;
  healthText: string | null;
  keyNumbers: KpiExecBriefKeyNumber[];
  forwardIndicators: string | null;
  growthPosture: string | null;
  changes: XFactorPoint[];
  noMaterialRisks: boolean;
  landminePosture: string | null;
  landmines: XFactorPoint[];
  motion: string | null;
  urgency: string | null;
  whatItMeans: string | null;
  motionRationale: string | null;
  valueConcentration: string[];
  caution: string | null;
  playbook: XFactorMove[];
  dropped: XFactorPoint[];
  questions: XFactorPoint[];
};

const str = (v: unknown): string | null =>
  typeof v === "string" && v.trim() ? v.trim() : null;

const num = (v: unknown): number | null =>
  typeof v === "number" && Number.isFinite(v) ? v : null;

const dicts = (v: unknown, max: number): Record<string, unknown>[] =>
  (Array.isArray(v) ? v : []).filter(isDict).slice(0, max);

const point = (
  title: string | null,
  body: string | null,
  extra: Partial<XFactorPoint> = {}
): XFactorPoint => ({
  title,
  body,
  tag: null,
  chip: null,
  implication: null,
  outcome: null,
  timeframe: null,
  refs: [],
  ...extra,
});

const PLAY_WHEN: Record<string, string> = {
  "this week": "This week",
  "this month": "This month",
  "this quarter": "This quarter",
};

const DIRECTIONS = ["up", "down", "flat", "unknown"] as const;

/** The overall pane, from a native view=category envelope or a bare pane. */
const overallPane = (data: unknown): Record<string, unknown> | null => {
  if (!isDict(data)) return null;
  const inner = isDict(data.briefing) ? data.briefing : null;
  if (inner && isDict(inner.overall)) return inner.overall;
  if (inner && isDict(inner.category)) return inner.category;
  if (isDict(data.category)) return data.category;
  return data;
};

const EMPTY: KpiExecBriefModel = {
  present: false,
  asOf: null,
  headline: null,
  whyTheseScores: null,
  tiles: [],
  landscapeText: null,
  coverage: [],
  scorecard: [],
  healthTrend: null,
  healthText: null,
  keyNumbers: [],
  forwardIndicators: null,
  growthPosture: null,
  changes: [],
  noMaterialRisks: false,
  landminePosture: null,
  landmines: [],
  motion: null,
  urgency: null,
  whatItMeans: null,
  motionRationale: null,
  valueConcentration: [],
  caution: null,
  playbook: [],
  dropped: [],
  questions: [],
};

export const buildKpiExecBrief = (data: unknown): KpiExecBriefModel => {
  const pane = overallPane(data);
  const brief =
    pane && isDict(pane.executiveBrief) ? pane.executiveBrief : null;
  if (!pane || !brief) return EMPTY;

  const bubble = isDict(pane.bubble) ? pane.bubble : {};
  const glance = isDict(brief.situationAtAGlance)
    ? brief.situationAtAGlance
    : {};
  const echo = isDict(glance.scores) ? glance.scores : {};
  const landscape = isDict(brief.kpiLandscape) ? brief.kpiLandscape : {};
  const health = isDict(brief.performanceHealth) ? brief.performanceHealth : {};
  const landmines = isDict(brief.landmines) ? brief.landmines : {};
  const rel = isDict(brief.relationshipImplications)
    ? brief.relationshipImplications
    : {};
  const envelope = isDict(data) && isDict(data.briefing) ? data.briefing : {};

  // xfactor shows Overall and Health from the same computed overall score.
  const overall = num(bubble.categoryScore) ?? num(echo.overall);

  return {
    present: true,
    asOf:
      str(glance.measurementDate) ||
      str(pane.measurementDate) ||
      str(envelope.measurementDate),
    headline: str(glance.headline),
    whyTheseScores: str(glance.whyTheseScores),
    tiles: [
      { label: "Overall", value: overall, rationale: null },
      {
        label: "Health",
        value: overall ?? num(echo.health),
        rationale: null,
      },
      {
        label: "Risk",
        value: num(bubble.riskScore) ?? num(echo.risk),
        rationale: null,
      },
      {
        label: "Opportunity",
        value: num(bubble.opportunityScore) ?? num(echo.opportunity),
        rationale: null,
      },
    ],
    landscapeText: str(landscape.text),
    coverage: dicts(landscape.coverage, 8)
      .map(c => ({
        category: str(c.category) || "",
        status: str(c.status),
        note: str(c.note),
      }))
      .filter(c => c.category),
    scorecard: dicts(brief.categoryScorecard, 12)
      .map(r => ({
        category: str(r.category) || "",
        score: num(r.score),
        trend: str(r.trend),
        weight: num(r.weight),
        readThrough: str(r.readThrough),
      }))
      .filter(r => r.category),
    healthTrend: str(health.trend),
    healthText: str(health.text),
    keyNumbers: dicts(health.keyNumbers, 6)
      .map(k => {
        const d = str(k.direction);
        return {
          signal: str(k.signal) || "",
          reading: str(k.reading) || "",
          direction: DIRECTIONS.find(x => x === d) ?? null,
          category: str(k.source_category),
        };
      })
      .filter(k => k.signal || k.reading),
    forwardIndicators: str(health.forwardIndicators),
    growthPosture: str(health.growthPosture),
    changes: dicts(brief.whatsChanging, 6)
      .map(c =>
        point(str(c.title), str(c.change), {
          implication: str(c.whyItMatters),
          implicationLabel: "Why it matters",
          chip: str(c.category),
          tag: str(c.source_category),
        })
      )
      .filter(p => p.title || p.body),
    noMaterialRisks: landmines.noMaterialRisks === true,
    landminePosture: str(landmines.posture),
    landmines: dicts(landmines.items, 4)
      .map(m =>
        point(str(m.risk), str(m.soWhat), {
          chip: str(m.status),
          tag: str(m.source_category),
        })
      )
      .filter(p => p.title),
    motion: str(rel.recommendedMotion),
    urgency: str(rel.urgency),
    whatItMeans: str(rel.whatItMeans),
    motionRationale: str(rel.motionRationale),
    valueConcentration: dicts(rel.valueConcentration, 4)
      .map(v => str(v.point))
      .filter((p): p is string => Boolean(p)),
    caution: str(rel.caution),
    playbook: dicts(brief.playbook, 5)
      .map(p => ({
        rank: num(p.rank) ?? Number.MAX_SAFE_INTEGER,
        move: {
          timeframe: PLAY_WHEN[(str(p.when) || "").toLowerCase()] ?? null,
          action: str(p.action),
          rationale: str(p.rationale),
          owner: str(p.owner),
          outcome: str(p.expectedOutcome),
          trigger: str(p.trigger),
          tag: str(p.source_category),
          priority: null,
          score: null,
        } as XFactorMove,
      }))
      .filter(p => p.move.action)
      .sort((a, b) => a.rank - b.rank)
      .map(p => p.move),
    dropped: dicts(brief.playbookDroppedItems, 3)
      .map(d => point(str(d.item), str(d.whyNot)))
      .filter(p => p.title),
    questions: dicts(brief.openQuestions, 5)
      .map(q =>
        point(str(q.question), str(q.why), { tag: str(q.source_category) })
      )
      .filter(p => p.title),
  };
};
